import { initializeApp } from 'firebase/app';
import { getMessaging, onMessage, getToken, isSupported } from 'firebase/messaging';
import { appearNotification, notifications } from '@/global/state/NotificationState';
import { commands, organization } from '@/global/state/GlobalState';
import axiosClient from '@/api/axios';

const firebaseConfig = {
    apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
    authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.VUE_APP_FIREBASE_APP_ID,
    measurementId: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID,
};

const VAPID_KEY = process.env.VUE_APP_FIREBASE_VAPID_KEY || 'BMO5D3aM00o8csdPvxIFkMLZa6ad0aOswN3cl-uQVl-NbgZB8dqsWWeKSkGbN4hA9N-NgsrcD6yS1CMNPhjp04g';

export const CUSTOMER_REQUEST_TYPES = ['call_waiter', 'order_sauce', 'order_utensils', 'order_bill'];
export const ORDER_NOTIFICATION_TYPES = ['new_order', 'order_status', 'kitchen_status', 'counter_status'];

// ---------- IndexedDB ----------

const dbName = 'notificationsDB';
const dbVersion = 1;
let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
    if (!dbPromise) {
        dbPromise = new Promise((resolve, reject) => {
            const request = indexedDB.open(dbName, dbVersion);

            request.onupgradeneeded = (event: any) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains('notifications')) {
                    db.createObjectStore('notifications', { keyPath: 'id', autoIncrement: true });
                }
            };

            request.onsuccess = (event: any) => resolve(event.target.result);
            request.onerror = (event: any) => reject(event.target.error);
        });
    }
    return dbPromise;
}

async function getStore(mode: IDBTransactionMode) {
    const db = await openDB();
    return db.transaction(['notifications'], mode).objectStore('notifications');
}

export async function addNotification(notificationData: any): Promise<number> {
    const store = await getStore('readwrite');
    return new Promise((resolve, reject) => {
        const request = store.add(notificationData);
        request.onsuccess = () => resolve(request.result as number);
        request.onerror = () => reject(request.error);
    });
}

export async function markAllAsRead() {
    const store = await getStore('readwrite');
    const request = store.getAll();
    request.onsuccess = () => {
        for (const record of request.result) {
            if (!record.is_read) store.put({ ...record, is_read: true });
        }
    };
}

export async function deleteNotification(notificationId: number) {
    const store = await getStore('readwrite');
    store.delete(notificationId);
}

export async function clearNotifications() {
    const store = await getStore('readwrite');
    store.clear();
}

// ---------- Payload da API -> formato das notificações do app ----------

const toNumber = (value?: string) => (value === 'true' ? 1 : parseInt(value || '0') || 0);

export function buildNotification(data: Record<string, string>): any | null {
    const base = {
        title: data.title,
        message: data.body,
        date: new Date(),
        organization_id: organization.value?.id ?? data.organizationId,
        type: data.type,
        is_read: false,
    };

    if (CUSTOMER_REQUEST_TYPES.includes(data.type)) {
        const request: any = {
            ...base,
            customerCpf: data.customerCpf,
            customerName: data.customerName,
            tableNumber: data.tableNumber,
        };

        // Mantém o formato "item:quantidade" usado pela lista de notificações
        if (data.type == 'order_sauce') {
            request.body = [
                `ketchup:${toNumber(data.ketchupQuantity)}`,
                `mayonnaise:${toNumber(data.mayonnaiseQuantity)}`,
                `seasonedMayonnaise:${toNumber(data.seasonedMayonnaiseQuantity)}`,
            ];
        } else if (data.type == 'order_utensils') {
            request.body = ['spoon', 'fork', 'knife', 'salt', 'napkin', 'glass', 'plate']
                .map((key) => `${key}:${toNumber(data[key])}`);
        } else if (data.type == 'order_bill') {
            request.payment_method = data.paymentMethod;
        }

        return request;
    }

    if (data.type == 'new_order' || data.type == 'order_status') {
        return {
            ...base,
            order_id: data.orderId,
            new_status: data.newStatus == 'canceled' ? 'cancelled' : data.newStatus,
        };
    }

    return null;
}

// ---------- Firebase Messaging ----------

export function getDeviceFingerprint(): string {
    const stored = localStorage.getItem('deviceFingerprint');
    if (stored) return stored;

    const cryptoApi: any = typeof crypto !== 'undefined' ? crypto : null;
    const fingerprint: string = cryptoApi?.randomUUID
        ? cryptoApi.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem('deviceFingerprint', fingerprint);
    return fingerprint;
}

function refreshCommands() {
    axiosClient.get('/tabs')
        .then((res: any) => {
            const _commands: any = {};
            const tabs = res.data.data || res.data;
            if (Array.isArray(tabs)) {
                tabs.forEach((tab: any) => {
                    if (!_commands[tab.number]) _commands[tab.number] = [];
                    _commands[tab.number].push(tab);
                });
            }
            commands.value = _commands;
        });
}

let initialized = false;

// Remove o vínculo deste aparelho com o usuário logado (chamar antes de limpar a sessão)
export async function unregisterPushNotifications() {
    initialized = false;
    const fingerprint = localStorage.getItem('deviceFingerprint');
    if (!fingerprint) return;

    try {
        await axiosClient.delete('/push-notification-tokens', { data: { fingerprint } });
    } catch (error) {
        console.error('Erro ao remover token de notificação:', error);
    }
}

export async function initPushNotifications() {
    if (initialized) return;
    initialized = true;

    try {
        if (!('Notification' in window) || !(await isSupported())) {
            console.warn('Notificações push não são suportadas neste navegador.');
            return;
        }

        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
            console.warn('Permissão para notificações negada.');
            return;
        }

        const messaging = getMessaging(initializeApp(firebaseConfig));

        onMessage(messaging, async (payload: any) => {
            const data = payload.data || {};
            const notificationData = buildNotification(data);

            if (notificationData) {
                notificationData.id = await addNotification(notificationData);
                notifications.value.push(notificationData);
                appearNotification(notificationData.title, notificationData.message);
            }

            if (CUSTOMER_REQUEST_TYPES.includes(data.type) || ORDER_NOTIFICATION_TYPES.includes(data.type)) {
                refreshCommands();
            }
        });

        const token = await getToken(messaging, { vapidKey: VAPID_KEY });
        if (!token) return;

        // A API faz upsert por usuário + dispositivo, então é seguro enviar a cada login
        await axiosClient.post('/push-notification-tokens', {
            token,
            fingerprint: getDeviceFingerprint(),
        });
    } catch (error) {
        console.error('Erro ao configurar notificações push:', error);
    }
}
